import "./PoiVideo.css";
import { Controller } from "@hotwired/stimulus";

type InlineVideo = HTMLVideoElement & {
    webkitEnterFullscreen?: () => void;
    webkitExitFullscreen?: () => void;
    webkitDisplayingFullscreen?: boolean;
};

export default class PoiVideoController extends Controller<HTMLElement> {
    private active = false;
    private playRevision = 0;
    private orientationLocked = false;

    connect(): void {
        this.active = true;
        this.video.addEventListener("playing", this.onPlaying);
        this.video.addEventListener("pause", this.onPause);
        this.video.addEventListener("waiting", this.onWaiting);
        this.video.addEventListener("ended", this.onEnded);
        this.video.addEventListener("error", this.onError);
        this.video.addEventListener("webkitbeginfullscreen", this.onFullscreenChange);
        this.video.addEventListener("webkitendfullscreen", this.onFullscreenChange);
        document.addEventListener("play", this.onMediaPlay, true);
        document.addEventListener("fullscreenchange", this.onFullscreenChange);
        document.addEventListener("turbo:before-visit", this.onLeave);
        document.addEventListener("turbo:before-cache", this.onLeave);
        this.setState("idle");
        this.onFullscreenChange();
    }

    disconnect(): void {
        this.active = false;
        this.onLeave();
        this.video.removeEventListener("playing", this.onPlaying);
        this.video.removeEventListener("pause", this.onPause);
        this.video.removeEventListener("waiting", this.onWaiting);
        this.video.removeEventListener("ended", this.onEnded);
        this.video.removeEventListener("error", this.onError);
        this.video.removeEventListener("webkitbeginfullscreen", this.onFullscreenChange);
        this.video.removeEventListener("webkitendfullscreen", this.onFullscreenChange);
        document.removeEventListener("play", this.onMediaPlay, true);
        document.removeEventListener("fullscreenchange", this.onFullscreenChange);
        document.removeEventListener("turbo:before-visit", this.onLeave);
        document.removeEventListener("turbo:before-cache", this.onLeave);
    }

    async toggle(): Promise<void> {
        const revision = ++this.playRevision;
        if (!this.video.paused || this.element.dataset.state === "loading") {
            this.video.pause();
            this.setState("paused");
            return;
        }
        this.status.hidden = true;
        if (this.element.dataset.state === "error") this.video.load();
        if (!this.video.hasAttribute("src")) this.video.src = this.video.dataset.src!;
        this.setState("loading");
        try {
            await this.video.play();
        } catch {
            if (this.active && revision === this.playRevision) this.onError();
        }
    }

    async toggleFullscreen(): Promise<void> {
        try {
            if (document.fullscreenElement === this.element) {
                await document.exitFullscreen();
            } else if (this.video.webkitDisplayingFullscreen) {
                this.video.webkitExitFullscreen?.();
            } else if (this.element.requestFullscreen) {
                await this.element.requestFullscreen();
                const orientation = screen.orientation as ScreenOrientation & { lock?: (value: string) => Promise<void> };
                if (orientation?.lock) {
                    try { await orientation.lock("landscape"); this.orientationLocked = true; } catch { /* Optional browser capability. */ }
                }
            } else if (this.video.webkitEnterFullscreen) {
                this.video.webkitEnterFullscreen();
            } else {
                this.showStatus(this.element.dataset.fullscreenErrorLabel!);
            }
        } catch {
            this.showStatus(this.element.dataset.fullscreenErrorLabel!);
        }
    }

    private readonly onPlaying = (): void => {
        if (this.active) this.setState("playing");
    };

    private readonly onPause = (): void => {
        if (!["idle", "error", "ended"].includes(this.element.dataset.state ?? "")) this.setState("paused");
    };

    private readonly onWaiting = (): void => {
        if (!this.video.paused) this.setState("loading");
    };

    private readonly onEnded = (): void => {
        this.setState("ended");
        void this.exitFullscreen();
    };

    private readonly onError = (): void => {
        this.setState("error");
        this.showStatus(this.element.dataset.errorLabel!);
    };

    private readonly onMediaPlay = (event: Event): void => {
        if (!(event.target instanceof HTMLMediaElement)) return;
        // The GPS permission unlock plays a silent source before any narration is selected.
        if (event.target.matches("audio[data-persistent-audio-player-target='audio']") && !event.target.dataset.mediaId) return;
        if (event.target === this.video) {
            for (const other of document.querySelectorAll<HTMLMediaElement>("audio, video")) {
                if (other !== this.video && !other.paused) other.pause();
            }
        } else if (!this.video.paused) this.video.pause();
    };

    private readonly onFullscreenChange = (): void => {
        const expanded = document.fullscreenElement === this.element || !!this.video.webkitDisplayingFullscreen;
        this.element.dataset.fullscreen = String(expanded);
        this.fullscreenButton.setAttribute("aria-label", expanded
            ? this.element.dataset.exitFullscreenLabel! : this.element.dataset.fullscreenLabel!);
        if (!expanded && this.orientationLocked) {
            screen.orientation?.unlock();
            this.orientationLocked = false;
        }
    };

    private readonly onLeave = (): void => {
        ++this.playRevision;
        this.setState("idle");
        this.video.pause();
        if (this.video.hasAttribute("src")) {
            this.video.removeAttribute("src");
            this.video.load();
        }
        this.status.hidden = true;
        void this.exitFullscreen();
    };

    private async exitFullscreen(): Promise<void> {
        try {
            if (document.fullscreenElement === this.element) await document.exitFullscreen();
            else if (this.video.webkitDisplayingFullscreen) this.video.webkitExitFullscreen?.();
        } catch { /* Navigation may already have closed fullscreen. */ }
        this.onFullscreenChange();
    }

    private setState(state: string): void {
        this.element.dataset.state = state;
        const playing = state === "playing";
        this.element.querySelector<HTMLButtonElement>("[data-poi-video-toggle]")!
            .setAttribute("aria-label", playing ? this.element.dataset.pauseLabel! : this.element.dataset.playLabel!);
        this.element.querySelector<HTMLElement>("[data-poi-video-play]")!.hidden = playing || state === "loading";
        this.element.querySelector<HTMLElement>("[data-poi-video-loading]")!.hidden = state !== "loading";
        this.element.querySelector<HTMLElement>("[data-poi-video-poster]")?.toggleAttribute("hidden", !["idle", "error", "ended"].includes(state));
        this.fullscreenButton.hidden = !["playing", "paused", "loading"].includes(state);
    }

    private showStatus(message: string): void {
        this.status.textContent = message;
        this.status.hidden = false;
    }

    private get video(): InlineVideo { return this.element.querySelector<InlineVideo>("[data-poi-video-element]")!; }
    private get fullscreenButton(): HTMLButtonElement { return this.element.querySelector<HTMLButtonElement>("[data-poi-video-fullscreen]")!; }
    private get status(): HTMLElement { return this.element.querySelector<HTMLElement>("[data-poi-video-status]")!; }
}
